import { Link } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useStore, formatINR, FREE_SHIPPING_THRESHOLD } from "@/context/StoreContext";

const Cart = () => {
  const {
    cart, subtotal, updateQty, removeFromCart, clearCart,
    proceedToCheckout, checkoutLoading,
  } = useStore();
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);

  return (
    <div data-testid="cart-page" className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24 min-h-[60vh]">
      <p className="overline text-[color:var(--vw-ink-soft)]">Ready to checkout</p>
      <h1 className="font-serif-display text-5xl md:text-7xl italic mt-3">Your Bag</h1>

      {cart.length === 0 ? (
        <div className="mt-16 text-center max-w-md mx-auto">
          <p className="font-serif-display text-3xl italic">Your bag is quiet.</p>
          <p className="mt-3 text-sm text-[color:var(--vw-ink-soft)]">Consider a slip dress, or something else that moves.</p>
          <Link to="/shop" className="inline-block mt-8 overline border-b border-[color:var(--vw-ink)] pb-1 hover:opacity-70">Explore The Edit</Link>
        </div>
      ) : (
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <ul className="lg:col-span-8 divide-y divide-[color:var(--vw-beige)]">
            {cart.map(i => (
              <li key={i.key} className="py-8 flex gap-6">
                <Link to={`/product/${i.handle}`} className="w-28 md:w-36 aspect-[4/5] bg-[color:var(--vw-bg-alt)] overflow-hidden flex-shrink-0">
                  <img src={i.image} alt={i.title} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 flex flex-col justify-between">
                  <div className="flex justify-between gap-4">
                    <div>
                      <Link to={`/product/${i.handle}`} className="font-serif-display text-2xl italic hover:opacity-70">{i.title}</Link>
                      <p className="text-xs font-utility text-[color:var(--vw-ink-soft)] mt-2">Size {i.size}</p>
                    </div>
                    <p className="font-utility font-medium whitespace-nowrap">{formatINR(i.price * i.quantity)}</p>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center border border-[color:var(--vw-beige-dark)]">
                      <button aria-label="Decrease" onClick={() => updateQty(i.key, -1)} className="p-2 hover:bg-[color:var(--vw-bg-alt)]"><Minus size={12} /></button>
                      <span className="w-8 text-center text-sm font-utility">{i.quantity}</span>
                      <button aria-label="Increase" onClick={() => updateQty(i.key, 1)} className="p-2 hover:bg-[color:var(--vw-bg-alt)]"><Plus size={12} /></button>
                    </div>
                    <button onClick={() => removeFromCart(i.key)} className="text-[color:var(--vw-ink-soft)] hover:text-[color:var(--vw-ink)] flex items-center gap-2 overline">
                      <Trash2 size={14} /> Remove
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <aside className="lg:col-span-4 border border-[color:var(--vw-beige)] p-8 bg-[color:var(--vw-bg-alt)] h-fit lg:sticky lg:top-28">
            <p className="overline mb-6">Order Summary</p>
            <div className="flex justify-between mb-3">
              <span className="text-sm">Subtotal</span>
              <span className="font-utility font-medium">{formatINR(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-[color:var(--vw-ink-soft)] mb-6">
              <span>Shipping</span>
              <span>{remaining > 0 ? "Calculated at checkout" : "Complimentary"}</span>
            </div>
            {remaining > 0 && (
              <p className="text-xs text-[color:var(--vw-ink-soft)] mb-6 border-t border-[color:var(--vw-beige-dark)] pt-4">
                Spend {formatINR(remaining)} more to unlock free shipping.
              </p>
            )}
            <button onClick={proceedToCheckout} disabled={checkoutLoading} className="w-full overline py-4 bg-[color:var(--vw-ink)] text-[color:var(--vw-bg)] hover:opacity-90 disabled:opacity-50 transition-opacity">
              {checkoutLoading ? "Preparing checkout…" : "Proceed to Checkout"}
            </button>
            <button onClick={clearCart} className="w-full overline mt-3 text-[color:var(--vw-ink-soft)] hover:text-[color:var(--vw-ink)]">
              Clear bag
            </button>
          </aside>
        </div>
      )}
    </div>
  );
};

export default Cart;
